/**
 * WebCrypto helpers shared by the studio (encrypt) and the viewer engine (decrypt).
 *
 * Segment wire format: `iv (12 bytes) || AES-128-GCM ciphertext+tag`.
 * Media segments are encrypted with additional authenticated data
 * `segmentAad(keyId, segmentId)` (= `${keyId}:${segmentId}`), binding the bytes
 * to their key and segment id.
 * The SHA-256 published in announces is computed over that whole buffer so relays
 * can verify integrity without holding the key.
 */

export const AES_GCM_IV_BYTES = 12;

type Bytes = ArrayBuffer | Uint8Array;

function toArrayBuffer(data: Bytes): ArrayBuffer {
  if (data instanceof ArrayBuffer) return data;
  return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
}

/** AES-GCM additional data for a media segment: `${keyId}:${segmentId}`. */
export function segmentAad(keyId: string, segmentId: string): string {
  return `${keyId}:${segmentId}`;
}

function gcmParams(iv: Uint8Array<ArrayBuffer>, aad?: string): AesGcmParams {
  return aad === undefined
    ? { name: 'AES-GCM', iv }
    : { name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(aad) as Uint8Array<ArrayBuffer> };
}

export async function sha256Hex(data: Bytes): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', toArrayBuffer(data));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function importAesKey(
  raw: Bytes,
  usages: KeyUsage[] = ['encrypt', 'decrypt'],
): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', toArrayBuffer(raw), { name: 'AES-GCM' }, false, usages);
}

/**
 * Encrypt with a fresh random 12-byte IV; returns `iv || ciphertext`.
 * `aad` (optional) must be passed identically to aesGcmDecrypt; use segmentAad() for segments.
 */
export async function aesGcmEncrypt(key: CryptoKey, plain: Bytes, aad?: string): Promise<ArrayBuffer> {
  const iv = crypto.getRandomValues(new Uint8Array(AES_GCM_IV_BYTES));
  const ct = await crypto.subtle.encrypt(gcmParams(iv, aad), key, toArrayBuffer(plain));
  const out = new Uint8Array(AES_GCM_IV_BYTES + ct.byteLength);
  out.set(iv, 0);
  out.set(new Uint8Array(ct), AES_GCM_IV_BYTES);
  return out.buffer;
}

/** Decrypt `iv || ciphertext`. Rejects on a wrong key, a different `aad`, or tampered data (GCM tag check). */
export async function aesGcmDecrypt(key: CryptoKey, data: Bytes, aad?: string): Promise<ArrayBuffer> {
  const bytes = new Uint8Array(toArrayBuffer(data));
  if (bytes.byteLength <= AES_GCM_IV_BYTES) {
    throw new Error('AES-GCM payload too short');
  }
  const iv = bytes.slice(0, AES_GCM_IV_BYTES);
  const ct = bytes.slice(AES_GCM_IV_BYTES);
  return crypto.subtle.decrypt(gcmParams(iv, aad), key, ct);
}

/** Decode a base64 string (control-plane keys) to bytes. */
export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
